// ============================================================
// Chess Arena — Ability Effect Handler
// ============================================================
// Handles execution of ability effects (Bomb, Freeze, Shield,
// Lightning), targeting mode UI, and timed effect expiry.
// Extracted from BattleScene to keep ability logic isolated.
// ============================================================

import Phaser from 'phaser';
import { GridPos, BOARD_COLS, BOARD_ROWS } from '../core/types';
import { Board } from '../core/board';
import { Piece } from '../core/piece';
import { PieceManager } from './pieceManager';
import { AbilityManager } from './abilityManager';
import eventBus from '../core/eventBus';

// -----------------------------------------------------------------
// Config passed at init
// -----------------------------------------------------------------

export interface AbilityEffectHandlerConfig {
  scene: Phaser.Scene;
  board: Board;
  pieceManager: PieceManager;
  abilityManager: AbilityManager;
  cellSize: number;
  cellH: number;
  boardOriginX: number;
  boardOriginY: number;
  onMatchResult: (result: 'victory' | 'defeat') => void;
  toggleTutorialOverlay: (interacting: boolean) => void;
  riggedBombTarget?: GridPos;
}

// -----------------------------------------------------------------
// AbilityEffectHandler
// -----------------------------------------------------------------

export class AbilityEffectHandler {
  private scene!: Phaser.Scene;
  private board!: Board;
  private pieceManager!: PieceManager;
  private abilityManager!: AbilityManager;
  private cellSize = 96;
  private cellH = 91;
  private boardOriginX = 0;
  private boardOriginY = 0;
  private onMatchResult!: (result: 'victory' | 'defeat') => void;
  private toggleTutorialOverlay!: (interacting: boolean) => void;
  private riggedBombTarget?: GridPos;

  // Targeting mode state
  targetingOverlay: Phaser.GameObjects.Graphics | null = null;
  isTargetingMode = false;
  targetingEnteredThisFrame = false;

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(config: AbilityEffectHandlerConfig): void {
    this.scene = config.scene;
    this.board = config.board;
    this.pieceManager = config.pieceManager;
    this.abilityManager = config.abilityManager;
    this.cellSize = config.cellSize;
    this.cellH = config.cellH;
    this.boardOriginX = config.boardOriginX;
    this.boardOriginY = config.boardOriginY;
    this.onMatchResult = config.onMatchResult;
    this.toggleTutorialOverlay = config.toggleTutorialOverlay;
    this.riggedBombTarget = config.riggedBombTarget;

    this.targetingOverlay = null;
    this.isTargetingMode = false;
    this.targetingEnteredThisFrame = false;
  }

  // ------------------------------------------------------------------
  // Bomb
  // ------------------------------------------------------------------

  handleBombEffect(): void {
    // Pick a random alive enemy piece (not King) as the target
    const enemies: Piece[] = [];
    for (const piece of this.pieceManager.allPieces()) {
      if (piece.pieceState.team === 'enemy' && piece.pieceState.isAlive && piece.pieceState.type !== 'King') {
        enemies.push(piece);
      }
    }
    if (enemies.length === 0) return;

    // Use rigged target for tutorial, otherwise pick random enemy
    let targetCell: GridPos;
    if (this.riggedBombTarget) {
      targetCell = { ...this.riggedBombTarget };
    } else {
      const target = enemies[Math.floor(Math.random() * enemies.length)];
      targetCell = { ...target.pieceState.pos };
    }

    // Brighten the board so the player can see the explosion clearly
    const flashOverlay = this.scene.add.graphics();
    flashOverlay.fillStyle(0xffffff, 0.12);
    flashOverlay.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
    flashOverlay.setDepth(19);

    // Highlight the 2x2 landing zone
    const landCol = Math.min(targetCell.col, BOARD_COLS - 2);
    const landRow = Math.min(targetCell.row, BOARD_ROWS - 2);
    const zoneGfx = this.scene.add.graphics();
    zoneGfx.setDepth(19);
    for (let dr = 0; dr < 2; dr++) {
      for (let dc = 0; dc < 2; dc++) {
        const px = Board.gridToPixel(
          { col: landCol + dc, row: landRow + dr },
          this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH,
        );
        zoneGfx.fillStyle(0xff4400, 0.25);
        zoneGfx.fillRect(px.x - this.cellSize / 2, px.y - this.cellH / 2, this.cellSize, this.cellH);
        zoneGfx.lineStyle(2, 0xff4400, 0.6);
        zoneGfx.strokeRect(px.x - this.cellSize / 2, px.y - this.cellH / 2, this.cellSize, this.cellH);
      }
    }

    // Create bomb projectile
    const playerKing = this.pieceManager.findPlayerKing();
    const startX = playerKing ? playerKing.x : this.scene.cameras.main.width / 2;
    const startY = playerKing ? playerKing.y : this.scene.cameras.main.height * 0.7;
    const targetPixel = Board.gridToPixel(targetCell, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);

    const bombSprite = this.scene.add.graphics();
    bombSprite.fillStyle(0x222222, 1);
    bombSprite.fillCircle(0, 0, 20);
    bombSprite.fillStyle(0x444444, 1);
    bombSprite.fillCircle(-3, -5, 14);
    bombSprite.fillStyle(0xff6600, 1);
    bombSprite.fillCircle(5, -16, 7); // fuse spark
    bombSprite.fillStyle(0xffcc00, 1);
    bombSprite.fillCircle(5, -16, 4);
    bombSprite.setPosition(startX, startY);
    bombSprite.setDepth(26);

    // Slow arc toss -- 1.2 seconds so player can follow it
    const flightDuration = 1200;
    const arcHeight = 180;
    const dy = targetPixel.y - startY;

    this.scene.tweens.add({
      targets: bombSprite,
      x: targetPixel.x,
      duration: flightDuration,
      ease: 'Linear',
    });

    // Separate y tween for arc + spin
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: flightDuration,
      ease: 'Linear',
      onUpdate: (tween) => {
        const p = tween.getValue() ?? 0;
        // Parabolic arc: y follows straight line + sine arc
        const linearY = startY + dy * p;
        const arc = Math.sin(p * Math.PI) * -arcHeight;
        bombSprite.setY(linearY + arc);
        // Spin the bomb
        bombSprite.setRotation(p * Math.PI * 3);
      },
      onComplete: () => {
        bombSprite.destroy();
        flashOverlay.destroy();
        zoneGfx.destroy();
        // Explode at the target cell -- pieces that moved away survive
        this.executeBombExplosion(targetCell);
      },
    });
  }

  // ------------------------------------------------------------------
  // Bomb explosion
  // ------------------------------------------------------------------

  executeBombExplosion(center: GridPos): void {
    // 2x2 area: the target cell + right + down + diagonal
    // Pick the 2x2 block that best fits within the board
    const startCol = Math.min(center.col, BOARD_COLS - 2);
    const startRow = Math.min(center.row, BOARD_ROWS - 2);

    let killedEnemyKing = false;

    const toDestroy: Piece[] = [];
    for (const piece of this.pieceManager.allPieces()) {
      if (!piece.pieceState.isAlive) continue;
      const pc = piece.pieceState.pos.col;
      const pr = piece.pieceState.pos.row;
      if (pc >= startCol && pc <= startCol + 1 && pr >= startRow && pr <= startRow + 1) {
        // Don't destroy player's own King
        if (piece.pieceState.team === 'player' && piece.pieceState.type === 'King') continue;
        toDestroy.push(piece);
      }
    }

    for (const piece of toDestroy) {
      this.pieceManager.removePiece(piece);

      if (piece.pieceState.type === 'King' && piece.pieceState.team === 'enemy') {
        eventBus.emit('king_captured', { capturedTeam: 'enemy', attackerId: 'bomb' });
        killedEnemyKing = true;
      }

      piece.capture();
    }

    // Explosion VFX
    const pixel = Board.gridToPixel(
      { col: startCol + 0.5, row: startRow + 0.5 },
      this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH,
    );
    const explosionGfx = this.scene.add.graphics();
    explosionGfx.fillStyle(0xff6600, 0.7);
    explosionGfx.fillCircle(pixel.x, pixel.y, this.cellSize * 1.2);
    explosionGfx.setDepth(20);

    // White flash ring
    const flashGfx = this.scene.add.graphics();
    flashGfx.lineStyle(4, 0xffff00, 0.9);
    flashGfx.strokeCircle(pixel.x, pixel.y, this.cellSize * 0.8);
    flashGfx.setDepth(21);

    this.scene.tweens.add({
      targets: [explosionGfx, flashGfx],
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 500,
      ease: 'Quad.easeOut',
      onComplete: () => {
        explosionGfx.destroy();
        flashGfx.destroy();
      },
    });

    if (killedEnemyKing) {
      this.onMatchResult('victory');
    }
  }

  // ------------------------------------------------------------------
  // Freeze
  // ------------------------------------------------------------------

  handleFreezeEffect(data?: Record<string, unknown>, elapsedTimeSec = 0): void {
    const durationSec = (data?.durationSec as number) ?? 4;

    for (const piece of this.pieceManager.allPieces()) {
      if (piece.pieceState.team === 'enemy' && piece.pieceState.isAlive) {
        piece.pieceState.isFrozen = true;
        piece.pieceState.frozenUntil = elapsedTimeSec + durationSec;
        // Visual: blue tint
        piece.sprite.setTint(0x6699ff);
      }
    }

    this.abilityManager.addActiveEffect('freeze', durationSec, elapsedTimeSec);
  }

  // ------------------------------------------------------------------
  // Shield
  // ------------------------------------------------------------------

  handleShieldEffect(data?: Record<string, unknown>, elapsedTimeSec = 0): void {
    const durationSec = (data?.durationSec as number) ?? 5;

    for (const piece of this.pieceManager.allPieces()) {
      if (piece.pieceState.team === 'player' && piece.pieceState.type === 'King' && piece.pieceState.isAlive) {
        piece.pieceState.isShielded = true;
        piece.pieceState.shieldedUntil = elapsedTimeSec + durationSec;
        // Visual: gold tint
        piece.sprite.setTint(0xffd700);
      }
    }

    this.abilityManager.addActiveEffect('shield', durationSec, elapsedTimeSec);
  }

  // ------------------------------------------------------------------
  // Lightning
  // ------------------------------------------------------------------

  handleLightningEffect(data?: Record<string, unknown>): void {
    const pieceId = data?.pieceId as string | undefined;
    if (!pieceId) return;

    const target = this.pieceManager.getPieceById(pieceId);
    if (!target || !target.pieceState.isAlive) return;
    if (target.pieceState.type === 'King') return; // Can't lightning the King

    // Destroy the piece directly
    this.pieceManager.removePiece(target);

    // Lightning VFX
    const pixel = { x: target.x, y: target.y };
    const lightningGfx = this.scene.add.graphics();
    lightningGfx.fillStyle(0xffff00, 0.8);
    lightningGfx.fillCircle(pixel.x, pixel.y, this.cellSize * 0.6);
    lightningGfx.setDepth(20);
    this.scene.tweens.add({
      targets: lightningGfx,
      alpha: 0,
      duration: 300,
      onComplete: () => lightningGfx.destroy(),
    });

    target.capture();

    eventBus.emit('piece_captured', {
      pieceType: target.pieceState.type,
      team: target.pieceState.team,
      attackerId: 'lightning',
    });
  }

  // ------------------------------------------------------------------
  // Freeze / Shield timer updates
  // ------------------------------------------------------------------

  updateAbilityEffects(elapsedTimeSec: number): void {
    for (const piece of this.pieceManager.allPieces()) {
      if (!piece.pieceState.isAlive) continue;

      // Clear expired freeze
      if (piece.pieceState.isFrozen && elapsedTimeSec >= piece.pieceState.frozenUntil) {
        piece.pieceState.isFrozen = false;
        piece.pieceState.frozenUntil = 0;
        piece.sprite.clearTint();
      }

      // Clear expired shield
      if (piece.pieceState.isShielded && elapsedTimeSec >= piece.pieceState.shieldedUntil) {
        piece.pieceState.isShielded = false;
        piece.pieceState.shieldedUntil = 0;
        piece.sprite.clearTint();
      }
    }

    // Update ability manager
    this.abilityManager.update(elapsedTimeSec);
  }

  // ------------------------------------------------------------------
  // Targeting mode
  // ------------------------------------------------------------------

  enterTargetingMode(): void {
    console.log('[AbilityEffectHandler] enterTargetingMode called, isTargetingMode was:', this.isTargetingMode);
    this.isTargetingMode = true;
    this.targetingEnteredThisFrame = true;

    // Hide tutorial overlay during targeting so the board is visible
    this.toggleTutorialOverlay(true);

    // Semi-transparent overlay above tutorial (depth 210)
    this.targetingOverlay = this.scene.add.graphics();
    this.targetingOverlay.fillStyle(0x000000, 0.3);
    this.targetingOverlay.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
    this.targetingOverlay.setDepth(210);

    // Highlight valid cells on the board
    const targeting = this.abilityManager.getTargetingState();
    if (targeting) {
      const gfx = this.targetingOverlay;
      if (targeting.targetingType === 'cell') {
        // Highlight all board cells
        for (let r = 0; r < BOARD_ROWS; r++) {
          for (let c = 0; c < BOARD_COLS; c++) {
            const px = Board.gridToPixel(
              { col: c, row: r },
              this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH,
            );
            gfx.fillStyle(0x44ff44, 0.3);
            gfx.fillRect(px.x - this.cellSize / 2, px.y - this.cellH / 2, this.cellSize, this.cellH);
            gfx.lineStyle(2, 0x44ff44, 0.6);
            gfx.strokeRect(px.x - this.cellSize / 2, px.y - this.cellH / 2, this.cellSize, this.cellH);
          }
        }
      } else if (targeting.targetingType === 'enemy_piece') {
        // Highlight enemy pieces (except King)
        for (const piece of this.pieceManager.allPieces()) {
          if (piece.pieceState.team === 'enemy' && piece.pieceState.isAlive && piece.pieceState.type !== 'King') {
            const px = Board.gridToPixel(
              piece.pieceState.pos,
              this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH,
            );
            gfx.fillStyle(0xff4444, 0.3);
            gfx.fillRect(px.x - this.cellSize / 2, px.y - this.cellH / 2, this.cellSize, this.cellH);
          }
        }
      }
    }
  }

  exitTargetingMode(): void {
    console.log('[AbilityEffectHandler] exitTargetingMode called', new Error().stack?.split('\n').slice(1, 4).join(' <- '));
    this.isTargetingMode = false;
    if (this.targetingOverlay) {
      this.targetingOverlay.destroy();
      this.targetingOverlay = null;
    }
    // Restore tutorial overlay if active
    this.toggleTutorialOverlay(false);
  }

  handleTargetingClick(worldX: number, worldY: number): boolean {
    if (!this.isTargetingMode) return false;

    const targeting = this.abilityManager.getTargetingState();
    if (!targeting) return false;

    // Convert screen position to grid using the same method as normal input
    const gridPos = Board.pixelToGrid(worldX, worldY, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);

    if (!gridPos) {
      // Clicked outside board -- only cancel if this isn't the same frame
      // that entered targeting mode (the button click propagates here)
      if (this.targetingEnteredThisFrame) {
        this.targetingEnteredThisFrame = false;
        return true; // swallow the click, don't cancel
      }
      this.abilityManager.cancelTargeting();
      return true;
    }

    if (targeting.targetingType === 'cell') {
      this.exitTargetingMode();
      this.abilityManager.confirmTarget(gridPos);
      return true;
    } else if (targeting.targetingType === 'enemy_piece') {
      // Find enemy piece at this cell
      const cellContent = this.board.getCellContent(gridPos);
      if (cellContent?.pieceId) {
        const piece = this.pieceManager.getPieceById(cellContent.pieceId);
        if (piece && piece.pieceState.team === 'enemy' && piece.pieceState.type !== 'King') {
          this.exitTargetingMode();
          this.abilityManager.confirmTarget(gridPos, cellContent.pieceId);
          return true;
        }
      }
      return true; // consumed the click even if invalid target
    }

    return false;
  }

  // ------------------------------------------------------------------
  // Frame helpers
  // ------------------------------------------------------------------

  /** Reset the targeting-entered-this-frame flag. Called by BattleScene each frame. */
  resetTargetingFrame(): void {
    this.targetingEnteredThisFrame = false;
  }

  // ------------------------------------------------------------------
  // Cleanup
  // ------------------------------------------------------------------

  destroy(): void {
    if (this.targetingOverlay) {
      this.targetingOverlay.destroy();
      this.targetingOverlay = null;
    }
    this.isTargetingMode = false;
    this.targetingEnteredThisFrame = false;
  }
}
